import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
// @mui
import { Box, Typography, CircularProgress, Avatar, Skeleton } from '@mui/material';
// components
import DemoBlur from '../DemoBlur';
import { InitialAvatar } from '../launchpad/LaunchpadAtoms';
import { LP } from '../launchpad/launchpadStyles';
// redux
import { useSelector, dispatch } from '../../redux/store';
import { fetchUserPerformanceData } from '../../redux/slices/userPerformance';
import { getPracticeUserList } from '../../redux/slices/practiceUser';

// ──────────────────────────────────────────────────────────────────────────────
// BookingPodiumWidget — reusable top-3 podium for user-performance leaderboards.
//
// variant='panel' (default): full-width launchpad-style panel with header and
//   spacious medal rows. Used on the Attended page below the ActionCards row
//   (task #35 — 'treatment_started').
//
// variant='widget': compact single card matching ActionCard exactly
//   (flex:1 1 0, minWidth:140px, minHeight:110px, same border/radius/padding).
//   Used inline in the Booked widget row as the 3rd card alongside the two
//   BookedTableReportsWidget ActionCards (task #34 — 'appointments_booked').
//
// Fetches via the existing fetchUserPerformanceData thunk (comparison='off',
// this-month window). The practiceUser slice must already be populated (it
// loads on every practice switch). Cross-references avatar from
// practiceUser by practice_user_id for staff photos.
// ──────────────────────────────────────────────────────────────────────────────

// Medal config — gold/silver/bronze tints for the flat rank numeral.
const MEDALS = [
    { rank: 1, color: '#d4900a', label: '1st' },  // warm gold
    { rank: 2, color: '#64748b', label: '2nd' },  // cool slate-silver
    { rank: 3, color: '#92400e', label: '3rd' },  // warm bronze-brown
];

// ── Medal rank badge — shared by both panel and widget rows ──────────────────
// Flat coloured rank numeral (1 / 2 / 3) in the gold/silver/bronze tint.
// No circle, no border — just the number. 1st place is slightly larger/bolder.
const MedalBadge = ({ medal, size }) => {
    const isLg = size !== 'sm';
    const isFirst = medal.rank === 1;
    // lg: 1st=18px, rest=14px. sm: 1st=13px, rest=11px.
    const FONT_SIZES = { lg: { first: '18px', rest: '14px' }, sm: { first: '13px', rest: '11px' } };
    const sizeKey = isLg ? 'lg' : 'sm';
    const fontSize = isFirst ? FONT_SIZES[sizeKey].first : FONT_SIZES[sizeKey].rest;
    const fontWeight = isFirst ? 900 : 700;
    // Fixed width keeps rows aligned regardless of digit width.
    const minWidth = isLg ? '20px' : '14px';

    return (
        <Box
            sx={{
                minWidth,
                flexShrink: 0,
                textAlign: 'center',
                fontSize,
                fontWeight,
                color: medal.color,
                lineHeight: 1,
            }}
        >
            {medal.rank}
        </Box>
    );
};

MedalBadge.propTypes = {
    medal: PropTypes.shape({
        rank: PropTypes.number,
        color: PropTypes.string,
    }).isRequired,
    size: PropTypes.oneOf(['lg', 'sm']),
};
MedalBadge.defaultProps = { size: 'lg' };

// ── Panel variant — spacious row ──────────────────────────────────────────────

const PodiumRow = ({ medal, name, profilePicture, count, label, isFirst }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            py: '9px',
            px: '8px',
            mx: '-8px',
            borderRadius: '9px',
            borderTop: isFirst ? '1px solid transparent' : `1px solid ${LP.line}`,
            transition: 'background-color 0.14s ease',
            '&:hover': { bgcolor: LP.hover },
        }}
    >
        <MedalBadge medal={medal} size='lg' />

        {/* Avatar — photo if available, initials fallback */}
        <DemoBlur sx={{ flexShrink: 0, display: 'inline-flex' }}>
            {profilePicture ? (
                <Avatar src={profilePicture} alt={name} sx={{ width: 30, height: 30, flexShrink: 0, fontSize: '11px' }} />
            ) : (
                <InitialAvatar name={name} />
            )}
        </DemoBlur>

        <Typography sx={{ flex: 1, fontSize: '13px', fontWeight: 600, color: LP.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <DemoBlur>{name}</DemoBlur>
        </Typography>

        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
            <Typography sx={{ fontSize: '15px', fontWeight: 800, color: medal.color, lineHeight: 1.1 }}>{count}</Typography>
            <Typography sx={{ fontSize: '10px', color: LP.sub, fontWeight: 600, lineHeight: 1.2 }}>{label}</Typography>
        </Box>
    </Box>
);

PodiumRow.propTypes = {
    medal: PropTypes.shape({ rank: PropTypes.number, color: PropTypes.string, label: PropTypes.string }).isRequired,
    name: PropTypes.string.isRequired,
    profilePicture: PropTypes.string,
    count: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    isFirst: PropTypes.bool,
};
PodiumRow.defaultProps = { profilePicture: null, isFirst: false };

// ── Widget variant — compact row fitting an ActionCard footprint ──────────────

const WidgetMedalRow = ({ medal, name, profilePicture, count, isFirst }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            pt: isFirst ? 0 : '5px',
            mt: isFirst ? 0 : '5px',
            borderTop: isFirst ? 'none' : '1px solid #f1f5f9',
        }}
    >
        <MedalBadge medal={medal} size='sm' />

        {/* Avatar */}
        <DemoBlur sx={{ flexShrink: 0, display: 'inline-flex' }}>
        {profilePicture ? (
            <Avatar src={profilePicture} alt={name} sx={{ width: 22, height: 22, flexShrink: 0, fontSize: '9px' }} />
        ) : (
            <Box
                sx={{
                    width: 22, height: 22, flexShrink: 0, borderRadius: '50%',
                    bgcolor: '#f1f5f9', border: '1px solid #e2e8f0',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '8px', fontWeight: 800, color: medal.color,
                }}
            >
                {(name || '?').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase() || '?'}
            </Box>
        )}
        </DemoBlur>

        <Typography sx={{ flex: 1, fontSize: '11px', fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <DemoBlur>{name}</DemoBlur>
        </Typography>

        <Typography sx={{ fontSize: '12px', fontWeight: 800, color: medal.color, flexShrink: 0 }}>
            {count}
        </Typography>
    </Box>
);

WidgetMedalRow.propTypes = {
    medal: PropTypes.shape({ rank: PropTypes.number, color: PropTypes.string, label: PropTypes.string }).isRequired,
    name: PropTypes.string.isRequired,
    profilePicture: PropTypes.string,
    count: PropTypes.number.isRequired,
    isFirst: PropTypes.bool,
};
WidgetMedalRow.defaultProps = { profilePicture: null, isFirst: false };

// ── Widget variant card shell ─────────────────────────────────────────────────
// Mirrors getCardStyle / skeletonCardStyle from ActionCards.jsx exactly.

const WIDGET_CARD_BASE = {
    flex: '1 1 0',
    minWidth: '140px',
    minHeight: '110px',
    padding: '16px',
    borderRadius: '12px',
    border: '2px solid #e2e8f0',
    background: '#fff',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    '@media (max-width: 576px)': {
        flex: '1 1 calc(50% - 6px)',
        minWidth: 'unset',
    },
};

// Skeleton for the widget card — 3 compact row placeholders
const WidgetSkeleton = () => (
    <Box sx={WIDGET_CARD_BASE}>
        <Skeleton variant='text' width='70%' height={14} sx={{ mb: '8px', borderRadius: '4px' }} />
        {[0, 1, 2].map((i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: '6px', mt: i > 0 ? '5px' : 0 }}>
                <Skeleton variant='circular' width={20} height={20} />
                <Skeleton variant='circular' width={22} height={22} />
                <Skeleton variant='text' sx={{ flex: 1, borderRadius: '4px' }} height={12} />
                <Skeleton variant='text' width={20} height={12} sx={{ borderRadius: '4px' }} />
            </Box>
        ))}
    </Box>
);

// ── Main component ────────────────────────────────────────────────────────────

const BookingPodiumWidget = ({ practiceId, rankField, title, countLabel, variant }) => {
    const isWidget = variant === 'widget';

    const { userPerformance, isLoading, error, _userPerfFetchKey } = useSelector(
        (s) => s.userPerformance,
    );
    const practiceUsers = useSelector((s) => s.practiceUser.practiceUser.results || []);

    // This-month window: 1st of month → today
    const startDate = moment().startOf('month').format('YYYY-MM-DD');
    const endDate = moment().format('YYYY-MM-DD');

    // Cache key mirrors fetchUserPerformanceData's own format
    const cacheKey = `${practiceId}-${startDate}-${endDate}-off`;

    useEffect(() => {
        if (!practiceId) return;
        if (_userPerfFetchKey === cacheKey) return;
        dispatch(fetchUserPerformanceData(practiceId, { start_date: startDate, end_date: endDate }, 'off'));
    // endDate and cacheKey intentionally omitted: startDate (1st of month) is the
    // meaningful boundary — endDate is always today and changes on midnight, but the
    // _userPerfFetchKey===cacheKey early-return still gates duplicate fetches correctly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceId, startDate]);

    // Ensure practice users are loaded so avatarMap can resolve profile_picture.
    // The Booked and Attended pages don't dispatch getPracticeUserList themselves,
    // so the slice is empty there unless we fetch it here. Guard: skip if results
    // are already populated (the slice clears on practice switch via clearData,
    // so a non-empty list means we have the right practice's data). callApi's
    // 8s GET dedupe prevents double-fetching if another component triggered it.
    useEffect(() => {
        if (!practiceId) return;
        if (practiceUsers.length > 0) return;
        dispatch(getPracticeUserList(practiceId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceId]);

    // practice_user_id → profile_picture
    const avatarMap = useMemo(() => {
        const m = {};
        practiceUsers.forEach((pu) => {
            if (pu.user?.avatar) m[pu.id] = pu.user.avatar;
        });
        return m;
    }, [practiceUsers]);

    // Top-3 by rankField desc, at least 1 count
    const top3 = useMemo(() => {
        if (!Array.isArray(userPerformance) || userPerformance.length === 0) return [];
        return [...userPerformance]
            .sort((a, b) => (b[rankField] || 0) - (a[rankField] || 0))
            .slice(0, 3)
            .filter((u) => (u[rankField] || 0) > 0);
    }, [userPerformance, rankField]);

    // ── Widget variant ────────────────────────────────────────────────────────
    if (isWidget) {
        if (isLoading) return <WidgetSkeleton />;

        if (error || top3.length === 0) {
            return (
                <Box sx={WIDGET_CARD_BASE}>
                    <Typography sx={{ fontSize: '12px', color: '#64748b', lineHeight: 1.3, mb: '8px' }}>
                        {title}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: 'auto' }}>
                        {error ? 'Could not load' : 'No activity this month'}
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={WIDGET_CARD_BASE}>
                <Typography sx={{ fontSize: '12px', color: '#64748b', lineHeight: 1.3, mb: '8px', fontWeight: 500 }}>
                    {title}
                </Typography>
                {top3.map((user, idx) => {
                    const medal = MEDALS[idx];
                    const name = [user.first_name, user.last_name].filter(Boolean).join(' ') || 'Unknown';
                    return (
                        <WidgetMedalRow
                            key={user.practice_user_id}
                            medal={medal}
                            name={name}
                            profilePicture={avatarMap[user.practice_user_id] || null}
                            count={user[rankField] || 0}
                            isFirst={idx === 0}
                        />
                    );
                })}
            </Box>
        );
    }

    // ── Panel variant (default — Attended page) ───────────────────────────────
    const panelSx = {
        bgcolor: 'background.paper',
        border: '1px solid #e9edf2',
        borderRadius: '16px',
        p: '6px 20px 16px',
        boxShadow: '0 1px 2px rgba(15,29,43,0.04), 0 6px 20px rgba(15,29,43,0.05)',
        display: 'flex',
        flexDirection: 'column',
    };

    if (isLoading) {
        return (
            <Box sx={{ ...panelSx, alignItems: 'center', justifyContent: 'center', minHeight: 130, py: 3 }}>
                <CircularProgress size={22} sx={{ color: LP.teal }} />
            </Box>
        );
    }

    if (error && top3.length === 0) {
        return (
            <Box sx={panelSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: '14px', pb: '8px' }}>
                    <Typography sx={{ fontSize: '11.5px', fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase', color: LP.teal }}>{title}</Typography>
                </Box>
                <Typography sx={{ fontSize: '12px', color: LP.red, py: 1 }}>Could not load leaderboard</Typography>
            </Box>
        );
    }

    if (top3.length === 0) {
        return (
            <Box sx={panelSx}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: '14px', pb: '8px' }}>
                    <Typography sx={{ fontSize: '11.5px', fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase', color: LP.teal }}>{title}</Typography>
                </Box>
                <Typography sx={{ fontSize: '12px', color: LP.sub, py: 1, opacity: 0.85 }}>No activity recorded this month</Typography>
            </Box>
        );
    }

    return (
        <Box sx={panelSx}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: '14px', pb: '8px' }}>
                <Typography sx={{ fontSize: '11.5px', fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase', color: LP.teal }}>{title}</Typography>
                <Typography sx={{ fontSize: '11px', color: LP.sub, fontWeight: 600 }}>This month</Typography>
            </Box>
            {top3.map((user, idx) => {
                const medal = MEDALS[idx];
                const name = [user.first_name, user.last_name].filter(Boolean).join(' ') || 'Unknown';
                return (
                    <PodiumRow
                        key={user.practice_user_id}
                        medal={medal}
                        name={name}
                        profilePicture={avatarMap[user.practice_user_id] || null}
                        count={user[rankField] || 0}
                        label={countLabel}
                        isFirst={idx === 0}
                    />
                );
            })}
        </Box>
    );
};

BookingPodiumWidget.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    rankField: PropTypes.string.isRequired,
    title: PropTypes.string,
    countLabel: PropTypes.string,
    variant: PropTypes.oneOf(['widget', 'panel']),
};

BookingPodiumWidget.defaultProps = {
    title: 'Top Bookers — This Month',
    countLabel: 'booked',
    variant: 'panel',
};

export default BookingPodiumWidget;
